import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../Components/Footer';
import { setPageTitle } from '../Utils/brand';
import { TelegramIcon, RobotIcon, ShieldIcon, QuestionCircleIcon, ChevronRightIcon, ExternalLinkIcon } from '../Components/Icons';

export default function ContactPage() {
    useEffect(() => {
        setPageTitle('Contact Us');
    }, []);

    const telegramContactLink = process.env.REACT_APP_TELEGRAM_CONTACT || (process.env.REACT_APP_TELEGRAM_BOT_USERNAME ? `https://t.me/${process.env.REACT_APP_TELEGRAM_BOT_USERNAME}` : 'https://t.me');
    const telegramChannelUsername = process.env.REACT_APP_TELEGRAM_CHANNEL_USERNAME || process.env.TELEGRAM_CHANNEL_USERNAME || '';
    const telegramChannelLink = process.env.REACT_APP_TELEGRAM_CHANNEL_URL || (telegramChannelUsername ? (telegramChannelUsername.startsWith('http') ? telegramChannelUsername : `https://t.me/${telegramChannelUsername.replace(/^@/, '')}`) : 'https://t.me');

    return (
        <main className="pt-24 min-h-screen flex flex-col justify-between">
            <section className="py-12 px-6 md:px-8 lg:px-16 flex-grow">
                <div className="max-w-4xl mx-auto space-y-10">
                    {/* Header */}
                    <div className="text-center space-y-3">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-neutral-300 text-xs font-medium tracking-wide">
                            <ShieldIcon className="w-3.5 h-3.5 text-sky-400" />
                            <span>Verified Communication Channels</span>
                        </div>
                        <h1 className="text-3xl md:text-5xl font-display font-extrabold text-white tracking-tight">
                            Contact & <span className="bg-gradient-to-r from-sky-400 via-indigo-300 to-purple-400 bg-clip-text text-transparent">Support</span>
                        </h1>
                        <p className="text-sm md:text-base text-neutral-400 max-w-xl mx-auto leading-relaxed">
                            Connect directly with our operations team or subscribe to our official Telegram channel for live domain availability drops.
                        </p>
                    </div>

                    {/* Cards Grid */}
                    <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                        {/* Direct Support Card */}
                        <div className="p-7 rounded-2xl bg-gradient-to-b from-neutral-900/90 via-neutral-900/50 to-neutral-950/90 border border-white/10 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1),0_12px_30px_rgba(0,0,0,0.5)] backdrop-blur-xl flex flex-col justify-between space-y-6 group hover:border-white/20 transition-all">
                            <div className="space-y-4">
                                <div className="w-12 h-12 bg-white/[0.04] border border-white/10 rounded-xl flex items-center justify-center text-sky-400">
                                    <RobotIcon className="w-6 h-6" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-white mb-1.5 flex items-center gap-2">
                                        Direct Support Bot
                                    </h2>
                                    <p className="text-neutral-400 text-xs sm:text-sm leading-relaxed">
                                        Personal assistance for domain acquisitions, custom escrow settlements, DNS routing, and rental questions.
                                    </p>
                                </div>
                            </div>

                            <a 
                                href={telegramContactLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full py-3 px-4 rounded-xl bg-white/[0.08] hover:bg-white/[0.14] border border-white/15 text-white text-xs sm:text-sm font-semibold inline-flex items-center justify-center gap-2 transition-all shadow-sm active:scale-[0.99]"
                            >
                                <TelegramIcon className="w-4 h-4 text-sky-400" />
                                <span>Open Telegram Support</span>
                                <ExternalLinkIcon className="w-3.5 h-3.5 text-neutral-400" />
                            </a>
                        </div>

                        {/* Telegram Channel Card */}
                        <div className="p-7 rounded-2xl bg-gradient-to-b from-neutral-900/90 via-neutral-900/50 to-neutral-950/90 border border-white/10 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1),0_12px_30px_rgba(0,0,0,0.5)] backdrop-blur-xl flex flex-col justify-between space-y-6 group hover:border-white/20 transition-all">
                            <div className="space-y-4">
                                <div className="w-12 h-12 bg-white/[0.04] border border-white/10 rounded-xl flex items-center justify-center text-emerald-400">
                                    <TelegramIcon className="w-6 h-6" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-white mb-1.5 flex items-center gap-2">
                                        Official Channel
                                    </h2>
                                    <p className="text-neutral-400 text-xs sm:text-sm leading-relaxed">
                                        Live channel broadcast: automated notifications when new domains are listed, acquired, rented, or released.
                                    </p>
                                </div>
                            </div>

                            <a 
                                href={telegramChannelLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full py-3 px-4 rounded-xl bg-white/[0.08] hover:bg-white/[0.14] border border-white/15 text-white text-xs sm:text-sm font-semibold inline-flex items-center justify-center gap-2 transition-all shadow-sm active:scale-[0.99]"
                            >
                                <TelegramIcon className="w-4 h-4 text-emerald-400" />
                                <span>Subscribe to Channel</span>
                                <ExternalLinkIcon className="w-3.5 h-3.5 text-neutral-400" />
                            </a>
                        </div>
                    </div>

                    {/* Official Security Notice */}
                    <div className="p-6 rounded-2xl bg-neutral-950/80 border border-white/10 max-w-2xl mx-auto space-y-4 text-center">
                        <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-200/90 text-xs leading-relaxed space-y-1.5">
                            <p className="font-semibold text-amber-300 flex items-center justify-center gap-1.5">
                                <ShieldIcon className="w-4 h-4 text-amber-400" />
                                Anti-Phishing Security Notice
                            </p>
                            <p>
                                Always verify you are contacting us strictly through links on this website. Our team will never initiate a private message or ask for private keys.
                            </p>
                        </div>

                        {/* FAQ Link */}
                        <div className="pt-2">
                            <Link 
                                to="/faq.html" 
                                className="inline-flex items-center gap-2 text-xs sm:text-sm text-neutral-400 hover:text-white transition-colors group"
                            >
                                <QuestionCircleIcon className="w-4 h-4 text-sky-400" />
                                <span>Have general questions? Visit our FAQ section</span>
                                <ChevronRightIcon className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
            
            <Footer />
        </main>
    );
}
